import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { NavigationService } from '@core/services/navigation/navigation.service';
import { ProductsService } from '@core/services/products/products.service';
import { CommandAvailabilityService } from '@shared/services/command-availability.service';

import { BaseProductCommand } from '@shared/commands/base-product.command';
import { IProductCommandContext } from '@shared/models/command';
import { ProductTypes } from '@shared/models/products.model';

/**
 * Команда для блокировки и разблокировки банковских карт
 *
 * Эта команда предоставляет пользователю возможность блокировать и разблокировать
 * банковские карты. Команда динамически изменяет свое поведение в зависимости
 * от текущего статуса карты - для заблокированных карт предлагает разблокировку,
 * для активных карт - блокировку.
 *
 * Особенности:
 * - Не поддерживает моментальные карты (для них блокировка недоступна)
 * - Динамически изменяет заголовок и тип операции
 * - Передает код продукта для корректной обработки на странице действия
 *
 * @example
 * ```typescript
 * // Для активной карты
 * const context = { data: { status: 'active', productCode: 'VISA_CLASSIC' } };
 * console.log(lockCommand.title); // "Заблокировать карту"
 * await lockCommand.execute(context);
 *
 * // Для заблокированной карты
 * const blockedContext = { data: { status: 'block', productCode: 'VISA_CLASSIC' } };
 * console.log(lockCommand.title); // "Разблокировать карту"
 * ```
 */
@Injectable({ providedIn: 'root' })
export class LockCardCommand extends BaseProductCommand {
  /** Флаг состояния карты - заблокирована ли карта в данный момент */
  private isBlocked = false;

  /**
   * Динамический тип команды в зависимости от статуса карты
   * @returns 'unlockCard' для заблокированных карт, 'lockCard' для активных
   */
  get type() {
    return this.isBlocked ? 'unlockCard' : 'lockCard';
  }

  /**
   * Динамический заголовок команды в зависимости от статуса карты
   * @returns соответствующий заголовок для блокировки/разблокировки
   */
  get title() {
    return this.isBlocked ? 'Разблокировать карту' : 'Заблокировать карту';
  }

  /** Иконка команды - замок, символизирующий блокировку */
  readonly icon = 'lock';

  /** Команда доступна только для карточных продуктов */
  readonly productType = ProductTypes.CARD;

  /** Флаг информационного действия */
  readonly isInfoAction = true;

  /** Настройки системы для проверки доступности блокировки и разблокировки */
  readonly settingsNames = ['REQUEST/LOCK_CARD', 'REQUEST/UNLOCK_CARD'];

  /** Порядок отображения в списке информационных действий */
  get order(): number {
    return this.getContextualOrder({
      [ProductTypes.CARD]: 90           // Карты: Заблокировать карту (9-й в списке "прочее")
    }, 999);
  }

  constructor(
    commandAvailabilityService: CommandAvailabilityService,
    private navigationService: NavigationService,
    private productsService: ProductsService
  ) {
    super(commandAvailabilityService);
  }

  /**
   * Выполняет команду блокировки/разблокировки карты
   *
   * Осуществляет навигацию на страницу выполнения действия с картой.
   * Передает код продукта в состоянии навигации для корректной обработки.
   *
   * @param context - контекст команды с информацией о карте
   */
  async execute(context: IProductCommandContext): Promise<void> {
    this.navigationService.push(`product/${context.data.id}/action/${this.type}`, {
      state: { productCode: context.data.productCode }
    });
  }

  /**
   * Проверяет доступность команды для конкретной карты
   *
   * Команда недоступна для моментальных карт. Также устанавливает
   * внутреннее состояние isBlocked на основе статуса карты.
   *
   * @param context - контекст команды с информацией о карте
   * @returns true если команда доступна для данной карты
   */
  protected checkAdditionalAvailability(context: IProductCommandContext): Observable<boolean> | boolean {
    // Проверяем, что это не момент-карта
    if (this.productsService.checkOnMomentCard(context.data)) {
      return false;
    }

    this.isBlocked = context.data.status === 'block';

    return true;
  }
}

